import React from 'react';
import { Label } from '../ui/label';

// Shared uncertainty treatment for extracted fields — used by BOTH the single-screen review and
// the documents modal, so the two never drift apart.
//
// Rule (per spec): fields below the confidence threshold get an amber label and amber border.
// Fields above it stay neutral — no percentage badge on every field.
export const CONFIDENCE_THRESHOLD = 0.8;

// field_confidence entries are either {confidence, uncertain, source} or a bare number, depending
// on when the document was extracted. Handle both rather than silently treating old ones as certain.
export function isFieldUncertain(doc, key) {
    if (!doc || !key) return false;
    const entry = (doc.field_confidence || {})[key];
    if (entry && typeof entry === 'object') {
        if (typeof entry.uncertain === 'boolean') return entry.uncertain;
        if (typeof entry.confidence === 'number') return entry.confidence <= CONFIDENCE_THRESHOLD;
    }
    if (typeof entry === 'number') return entry <= CONFIDENCE_THRESHOLD;
    const list = doc.uncertain_fields;
    return Array.isArray(list) ? list.includes(key) : false;
}

export function fieldInputClass(uncertain) {
    return `w-full h-10 rounded-lg border px-3 text-sm bg-background focus:outline-none focus:ring-2 ${
        uncertain ? 'border-amber-500 focus:ring-amber-500/30' : 'border-input focus:ring-ring/30'
    }`;
}

export function fieldLabelClass(uncertain) {
    return `text-xs ${uncertain ? 'text-amber-600 font-medium' : 'text-muted-foreground'}`;
}

// A labelled, uncertainty-aware field. `children` lets a caller drop in a Select instead of an
// input while keeping identical label/border treatment.
export function ConfidenceField({ label, uncertain = false, value, onChange, type = 'text',
                                 readOnly = false, testId, children, className = '' }) {
    return (
        <div className={className}>
            <Label className={fieldLabelClass(uncertain)}>{label}</Label>
            {children ? (
                <div className="mt-1">{children}</div>
            ) : (
                <input
                    className={`${fieldInputClass(uncertain)} mt-1`}
                    type={type}
                    step={type === 'number' ? '0.01' : undefined}
                    value={value ?? ''}
                    readOnly={readOnly}
                    onChange={readOnly ? undefined : (e) => onChange?.(
                        type === 'number' ? (e.target.value === '' ? '' : parseFloat(e.target.value)) : e.target.value)}
                    data-testid={testId}
                />
            )}
        </div>
    );
}


// ── VAT contradiction ───────────────────────────────────────────────────────────────────
// Mirrors classification_config.implies_vat / NO_VAT_CODES on the backend. Surfaces the case
// where the code asserts VAT but the amount is 0 (or the reverse) so the accountant sees the
// contradiction instead of inheriting it. Never blocks anything — it is a warning, not a gate.
export const NO_VAT_CODES = ['MOMSFRI', 'IKKEMOMS'];

export function impliesVat(vatCode) {
    const c = (vatCode || '').trim().toUpperCase();
    return !!c && !NO_VAT_CODES.includes(c);
}

export function vatContradiction(vatCode, vatAmount) {
    const code = (vatCode || '').trim();
    if (!code) return null;
    const amt = typeof vatAmount === 'number' ? vatAmount : parseFloat(vatAmount);
    if (!Number.isFinite(amt)) return null;          // no amount to compare against
    const zero = Math.abs(amt) < 0.005;
    if (impliesVat(code) && zero) {
        return `Momskoden ${code} angiver moms, men momsbeløbet er 0.`;
    }
    if (!impliesVat(code) && !zero) {
        return `Momskoden ${code} angiver ingen moms, men momsbeløbet er ${amt}.`;
    }
    return null;
}

export default ConfidenceField;
